// The campaign editor (UI-2): five steps, Klaviyo's order, our look.
//
//   /kampanyok/uj                         — step 1 only: name and mode
//   /kampanyok/:id/szerkesztes?lepes=...  — every step of an existing draft
//
// The step is in the address, so the browser's back button walks the steps and
// a link can open the review page directly. Every step can be opened in any
// order (the step bar is a table of contents, not a gate); the review step is
// where what is missing becomes a condition.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { routeId } from "@/lib/utils";
import { toast } from "sonner";
import { ExternalLinkIcon } from "lucide-react";
import { fetchLocalizations, fetchWordingMatrix, withdrawGroup } from "@/lib/api";
import type { CampaignGroupSummary, CountryWording, Localization } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Stepper } from "@/components/emc/Stepper";
import type { StepItem } from "@/components/emc/Stepper";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { campaignGroupShown, statusLook } from "@/components/emc/status";
import { usePageCrumb } from "@/components/emc/shell/crumb";
import { reviewChecks } from "@/pages/campaigns/checks";
import type { Check } from "@/pages/campaigns/checks";
import {
  useEstimate,
  useGroup,
  useHaltStatus,
  useRenders,
  useSendingIdentities,
} from "@/pages/campaigns/data";
import type { EstimateData } from "@/pages/campaigns/data";
import { AudienceStep } from "@/pages/campaigns/editor/AudienceStep";
import { BasicsStep } from "@/pages/campaigns/editor/BasicsStep";
import { ContentStep } from "@/pages/campaigns/editor/ContentStep";
import type { Editor } from "@/pages/campaigns/editor/context";
import { ReviewStep } from "@/pages/campaigns/editor/ReviewStep";
import { SummaryPanel } from "@/pages/campaigns/editor/SummaryPanel";
import { TimingStep } from "@/pages/campaigns/editor/TimingStep";
import {
  COMPOSING,
  STEPS,
  draftStarts,
  isLocal,
  isStepKey,
  loadTiming,
  localMomentFor,
  messageOf,
  nowIn,
  saveTiming,
  startsLine,
  startsText,
} from "@/pages/campaigns/model";
import type { StepKey, TimingDraft } from "@/pages/campaigns/model";

const DESCRIPTION =
  "Öt lépés: alapok, címzettek, tartalom, időzítés, áttekintés és küldés. Bármelyikhez vissza lehet lépni; semmi nem megy ki, amíg az utolsó lépésben meg nem erősíted.";

export default function CampaignEditor() {
  const params = useParams();
  if (params.id === undefined) return <NewCampaign />;
  const groupId = routeId(params.id);
  if (groupId === null) return <Navigate to="/kampanyok" replace />;
  return <ExistingCampaign key={groupId} groupId={groupId} />;
}

// --- a campaign that does not exist yet ------------------------------------------------

function NewCampaign() {
  const navigate = useNavigate();
  const { identities } = useSendingIdentities();
  usePageCrumb("Új kampány");
  const steps: StepItem<StepKey>[] = STEPS.map((step, index) => ({
    key: step.key,
    label: step.label,
    status: "todo",
    disabled: index > 0,
    hint: index === 0 ? "név és mód" : undefined,
  }));
  return (
    <>
      <PageHeader back={{ to: "/kampanyok", label: "Kampányok" }} title="Új kampány" description={DESCRIPTION} />
      <Stepper steps={steps} current="alapok" onSelect={() => undefined} className="mb-6" />
      <div className="max-w-content">
        <BasicsStep
          group={null}
          identities={identities}
          onCreated={(id) => navigate(`/kampanyok/${id}/szerkesztes?lepes=cimzettek`, { replace: true })}
          onNext={() => undefined}
        />
      </div>
    </>
  );
}

// --- an existing campaign -----------------------------------------------------------------

function defaultStep(summary: CampaignGroupSummary): StepKey {
  if (summary.countries.length === 0) return "cimzettek";
  if (summary.letter === null) return "tartalom";
  return "attekintes";
}

function stepItems(
  summary: CampaignGroupSummary,
  unapproved: string[],
  estimate: EstimateData,
  timing: TimingDraft,
  zoneOf: (country: string) => string,
  checks: Check[],
): StepItem<StepKey>[] {
  const rows = estimate.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.recipients, 0);
  const empty = rows.some((row) => row.recipients <= 0);
  const letter = summary.letter;
  const failing = checks.filter((check) => check.blocking && check.status === "fail").length;
  // Localised: both halves of the gate the schedule holds — every country's
  // letter, and in an A/B test every arm of every country. Counting only the
  // letters showed the step done while the arms still held the schedule.
  const waiting =
    summary.mode === "localized"
      ? [
          unapproved.length > 0 ? `${unapproved.length} ország` : null,
          summary.unapproved_arms.length > 0 ? `${summary.unapproved_arms.length} variáns` : null,
        ].filter((part): part is string => part !== null)
      : [];

  const content: Pick<StepItem<StepKey>, "status" | "hint"> =
    letter === null
      ? { status: "todo", hint: "nincs levél" }
      : letter.status !== "published"
        ? { status: "attention", hint: "a verzió nem publikált" }
        : waiting.length > 0
          ? { status: "attention", hint: `jóváhagyásra vár: ${waiting.join(", ")}` }
          : {
              status: "done",
              hint: `${letter.template_name} v${letter.version}${summary.ab !== null ? " · A/B" : ""}`,
            };

  const later = `${timing.date}T${timing.time}`;
  const past =
    timing.mode === "later" &&
    isLocal(later) &&
    summary.countries.some((row) => localMomentFor(timing, row.country, zoneOf(row.country)) <= nowIn(zoneOf(row.country)));
  const timingItem: Pick<StepItem<StepKey>, "status" | "hint"> =
    timing.mode === "now"
      ? { status: "done", hint: "most" }
      : !isLocal(later)
        ? { status: "todo", hint: "nincs időpont" }
        : past
          ? { status: "attention", hint: "az időpont elmúlt" }
          : {
              status: "done",
              hint: startsText(
                startsLine(
                  draftStarts(timing, summary.countries.map((row) => row.country), zoneOf),
                  zoneOf,
                ),
              ),
            };

  return [
    { key: "alapok", label: "Alapok", status: "done", hint: summary.name },
    {
      key: "cimzettek",
      label: "Címzettek",
      status: summary.countries.length === 0 ? "todo" : empty ? "attention" : "done",
      hint:
        summary.countries.length === 0
          ? "nincs ország"
          : `${summary.countries.length} ország${estimate.rows !== null ? ` · ${count(total)}` : ""}`,
    },
    { key: "tartalom", label: "Tartalom", ...content },
    { key: "idozites", label: "Időzítés", ...timingItem },
    {
      key: "attekintes",
      label: "Áttekintés és küldés",
      status: failing > 0 ? "attention" : "todo",
      hint: failing > 0 ? `${failing} tennivaló` : "indítás",
    },
  ];
}

function ExistingCampaign({ groupId }: { groupId: number }) {
  const [params, setParams] = useSearchParams();
  const group = useGroup(groupId);
  const { identities, identityOf, error: identityError } = useSendingIdentities();
  const estimate = useEstimate(groupId);
  const halt = useHaltStatus();
  const [timing, setTimingState] = useState<TimingDraft>(() => loadTiming(groupId));
  const [localizations, setLocalizations] = useState<Localization[] | null>(null);
  const [wording, setWording] = useState<CountryWording[] | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const { detail, summary } = group;
  usePageCrumb(summary?.name ?? null);

  const setTiming = useCallback(
    (next: TimingDraft) => {
      setTimingState(next);
      saveTiming(groupId, next);
    },
    [groupId],
  );

  const localized = summary?.mode === "localized";
  const reloadLocalizations = useCallback(async () => {
    if (!localized) return;
    try {
      const [rows, matrix] = await Promise.all([
        fetchLocalizations(groupId),
        fetchWordingMatrix(groupId),
      ]);
      setLocalizations(rows);
      setWording(matrix);
    } catch (raised: unknown) {
      toast.error("A fordítások nem töltődtek be", { description: messageOf(raised) });
    }
  }, [groupId, localized]);

  useEffect(() => {
    void reloadLocalizations();
  }, [reloadLocalizations]);

  const countryCodes = useMemo(() => (summary?.countries ?? []).map((row) => row.country), [summary]);
  // What each country's send renders. A localised country sends its own
  // letter with its approved wording on it — the schedule freezes exactly that
  // onto its campaign (4.3) — so its preview is that, not the group's letter.
  const letterId = summary?.letter?.template_version_id ?? null;
  const renders = useRenders(
    (country) => {
      if (!localized) return letterId === null ? null : { versionId: letterId };
      const row = wording?.find((entry) => entry.country === country);
      if (row === undefined || row.preview_version_id === null) return null;
      return {
        versionId: row.preview_version_id,
        subject: row.preview_subject,
        preheader: row.preview_preheader,
      };
    },
    countryCodes,
    (country) => identityOf(country)?.language ?? null,
  );

  const checks = useMemo(
    () =>
      detail !== null && summary !== null
        ? reviewChecks({
            detail,
            summary,
            estimate,
            halt,
            renders,
            localizations: localized ? localizations : null,
            timing,
            identityOf,
          })
        : [],
    [detail, summary, estimate, halt, renders, localizations, localized, timing, identityOf],
  );

  const requested = params.get("lepes");
  const step: StepKey | null = isStepKey(requested) ? requested : summary !== null ? defaultStep(summary) : null;
  const go = useCallback(
    (next: StepKey) => {
      setParams({ lepes: next });
      window.scrollTo({ top: 0 });
    },
    [setParams],
  );

  if (group.error !== null && summary === null) {
    return (
      <>
        <PageHeader back={{ to: "/kampanyok", label: "Kampányok" }} title="Kampány" />
        <ErrorState message={group.error} onRetry={() => void group.reload()} />
      </>
    );
  }
  if (detail === null || summary === null || step === null) {
    return (
      <>
        <PageHeader back={{ to: "/kampanyok", label: "Kampányok" }} title="Kampány" />
        <LoadingState lines={6} />
      </>
    );
  }

  const zoneOf = (country: string) => identityOf(country)?.timezone ?? "UTC";
  const editor: Editor = {
    groupId,
    detail,
    summary,
    reload: group.reload,
    identities,
    identityOf,
    estimate,
    halt,
    timing,
    setTiming,
    editable: summary.state === "draft",
    localizations: localized ? localizations : null,
    wording: localized ? wording : null,
    reloadLocalizations,
    renders,
    checks,
    go,
  };

  async function withdraw() {
    setWithdrawing(true);
    try {
      await withdrawGroup(groupId);
      await group.reload();
      toast.success("A kampány újra szerkeszthető");
    } catch (raised: unknown) {
      toast.error("Nem sikerült visszavenni szerkesztésre", { description: messageOf(raised) });
    } finally {
      setWithdrawing(false);
    }
  }

  return (
    <>
      <PageHeader
        back={{ to: "/kampanyok", label: "Kampányok" }}
        title={summary.name}
        documentTitle={`${summary.name} · szerkesztés`}
        meta={<StatusPill domain="campaignGroup" state={campaignGroupShown(summary)} title={summary.state} />}
        description={DESCRIPTION}
        actions={
          <Button variant="outline" asChild>
            <Link to={`/kampanyok/${groupId}`}>
              <ExternalLinkIcon />
              A kampány oldala
            </Link>
          </Button>
        }
      />

      <Stepper
        steps={stepItems(summary, detail.unapproved, estimate, timing, zoneOf, checks)}
        current={step}
        onSelect={go}
        className="mb-6"
      />

      {identityError !== null && <ErrorState className="mb-6" message={identityError} />}
      {summary.state === "review" && (
        <Callout tone="warn" className="mb-6">
          <span className="flex flex-wrap items-center justify-between gap-3">
            <span>
              A kampány ellenőrzésre vár: az országok, a levél és az A/B teszt le vannak zárva. Innen
              indítható, vagy visszavehető szerkesztésre.
            </span>
            <Button variant="outline" size="sm" type="button" disabled={withdrawing} onClick={() => void withdraw()}>
              Vissza szerkesztésre
            </Button>
          </span>
        </Callout>
      )}
      {!COMPOSING.has(summary.state) && (
        <Callout className="mb-6">
          A kampány állapota: {statusLook("campaignGroup", summary.state).label}. Szerkeszteni már
          nem lehet; a haladása a <Link to={`/kampanyok/${groupId}`}>kampány oldalán</Link> látszik.
        </Callout>
      )}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_calc(var(--spacing)*80)]">
        <div className="min-w-0">
          {step === "alapok" && (
            <BasicsStep group={summary} identities={identities} onCreated={() => undefined} onNext={() => go("cimzettek")} />
          )}
          {step === "cimzettek" && <AudienceStep editor={editor} />}
          {step === "tartalom" && <ContentStep editor={editor} />}
          {step === "idozites" && <TimingStep editor={editor} />}
          {step === "attekintes" && <ReviewStep editor={editor} />}
        </div>
        <aside className="xl:sticky xl:top-20">
          <SummaryPanel editor={editor} />
        </aside>
      </div>
    </>
  );
}
